import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { money, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, Store, Sparkles, Ticket, Pencil, X, Eye, Heart, Star, Megaphone, Download, Share2, Trophy, Clock, Users, Inbox, Phone, CalendarCheck, FileText, ChevronDown, ExternalLink, Link2, Copy, Zap, Upload } from "lucide-react";
import PostcardCanvas from "@/components/PostcardCanvas";
import { TermsAgree, TERMS_VERSION } from "@/components/TermsAgree";
import { TermsReconsentBanner } from "@/components/TermsReconsent";
import { GrowPanel } from "@/components/GrowPanel";
import { LeadStats } from "@/components/LeadStats";
import { PartnerKit } from "@/components/PartnerKit";

const STATUS_BADGE = { approved: "bg-sam-green", pending: "bg-sam-amber", rejected: "bg-slate-400", draft: "bg-slate-400" };

const AI_PACKS = [["100", "$20"], ["250", "$50"], ["500", "$100"], ["1000", "$200"]];

function AdProof({ business }) {
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState(null);
  const [needCredits, setNeedCredits] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [proof, setProof] = useState(null);
  const fileRef = React.useRef(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/my/businesses/${business.id}/postcard-slot`); setSlot(data.slot); }
    catch { setSlot(null); }
  }, [business.id]);
  useEffect(() => { load(); }, [load]);
  if (!slot) return null;
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/postcard-slots/${slot.id}/ad-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ad submitted for review");
      if (data.warning) toast.warning(data.warning);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const genCopy = async () => {
    setBusy(true); setNeedCredits(false);
    try {
      const { data } = await api.post(`/postcard-slots/${slot.id}/ai-ad-copy`, {});
      setCopy(data.copy);
      setSlot((s) => ({ ...s, ai_unlocked: true, ai_credits: data.ai_credits_left }));
      toast.success(data.charged ? `AI copy ready — ${data.ai_credits_left} credits left` : `AI copy ready — ${data.free_rounds_left} free rounds left`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && d.action === "unlock") {
        try { const { data } = await api.post(`/payments/ai-builder-unlock/${slot.id}`, { origin_url: window.location.origin }); if (data.checkout_url) { window.location.href = data.checkout_url; return; } toast.success("Already unlocked — try again"); }
        catch { toast.error("Could not start unlock"); }
      } else if (d && d.action === "credits") { setNeedCredits(true); toast.error("Out of AI credits — grab a pack to keep building."); }
      else { toast.error(typeof d === "string" ? d : "AI copy failed"); }
    } finally { setBusy(false); }
  };
  const buyPack = async (pack) => {
    setBusy(true);
    try { const { data } = await api.post(`/postcard-slots/${slot.id}/ai-credits-checkout`, { pack, origin_url: window.location.origin }); if (data.checkout_url) window.location.href = data.checkout_url; }
    catch { toast.error("Could not start checkout"); }
    finally { setBusy(false); }
  };
  const openProof = async () => {
    setProofOpen(true); setProof(null);
    try { const { data } = await api.get(`/my/businesses/${business.id}/postcard-proof`); setProof(data); }
    catch { setProof(null); }
  };
  const badge = { pending: ["bg-sam-amber", "In review"], approved: ["bg-sam-green", "Approved — printing"], rejected: ["bg-slate-400", "Changes requested"] }[slot.ad_status] || ["bg-slate-300", "No ad yet"];
  return (
    <div className="mt-4 rounded-xl border border-border bg-slate-50/60 p-4" data-testid={`ad-proof-${business.slug}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-display font-bold text-sam-navy text-sm">Your postcard ad</div>
        <Badge className={badge[0]}>{badge[1]}</Badge>
      </div>
      {slot.ad_reject_reason && <p className="text-xs text-sam-crimson mt-1">Reviewer note: {slot.ad_reject_reason}</p>}
      {slot.stats && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid={`ad-stats-${business.slug}`}>
          {[["QR scans", slot.stats.scans, "scans"], ["Ad clicks", slot.stats.clicks, "clicks"], ["Last 30 days", slot.stats.last_30_days, "last30"]].map(([l, v, k]) => (
            <div key={k} className="rounded-lg bg-white border border-border p-2 text-center">
              <div className="font-display text-xl font-black text-sam-navy" data-testid={`ad-stats-${k}-${business.slug}`}>{v}</div>
              <div className="text-[10px] uppercase tracking-wide text-sam-slate">{l}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-start gap-3 flex-wrap">
        {slot.ad_asset_id && <img src={`${API}/ad-assets/${slot.ad_asset_id}/file`} alt="your ad" className="h-20 w-28 object-contain bg-white rounded border" />}
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.pdf" onChange={upload} className="hidden" data-testid={`ad-file-${business.slug}`} />
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`ad-upload-${business.slug}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Upload finished ad</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} onClick={genCopy} data-testid={`ad-ai-${business.slug}`}><Sparkles className="h-3.5 w-3.5 mr-1" />Build copy with AI</Button>
          {slot.ad_asset_id && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={openProof} data-testid={`ad-preview-card-${business.slug}`}><Eye className="h-3.5 w-3.5 mr-1" />Preview on card</Button>}
        </div>
      </div>
      {slot.ai_unlocked && <p className="mt-2 text-[11px] text-sam-slate" data-testid={`ai-credits-${business.slug}`}><Zap className="inline h-3 w-3 text-sam-amber mr-0.5" />AI builder unlocked · <b className="text-sam-navy">{slot.ai_credits ?? 0}</b> credits left</p>}
      {needCredits && (
        <div className="mt-2 rounded-lg border border-sam-amber/50 bg-amber-50 p-2.5" data-testid={`ai-credit-packs-${business.slug}`}>
          <p className="text-xs font-semibold text-sam-navy mb-1.5">Out of AI credits — add more (10 credits per build):</p>
          <div className="flex flex-wrap gap-1.5">
            {AI_PACKS.map(([c, p]) => (
              <Button key={c} size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => buyPack(c)} data-testid={`ai-credit-pack-${c}-${business.slug}`}>{c} · {p}</Button>
            ))}
          </div>
        </div>
      )}
      {copy && (
        <div className="mt-3 text-xs bg-white rounded border p-2 space-y-0.5" data-testid={`ad-copy-${business.slug}`}>
          {copy.headline && <div><b>Headline:</b> {copy.headline}</div>}
          {copy.offer && <div><b>Offer:</b> {copy.offer}</div>}
          {copy.body && <div><b>Body:</b> {copy.body}</div>}
          {copy.cta && <div><b>CTA:</b> {copy.cta}</div>}
          {copy.raw && <div className="text-sam-slate">{copy.raw}</div>}
        </div>
      )}
      <p className="text-[11px] text-sam-slate mt-2">PNG, JPG or PDF. Only approved ads print and show on your live ZIP page.</p>
      <Dialog open={proofOpen} onOpenChange={setProofOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`ad-proof-dialog-${business.slug}`}>
          <DialogHeader>
            <DialogTitle>Your ad on the 9 × 12 postcard</DialogTitle>
            <DialogDescription>
              {slot.ad_status === "approved" ? "Approved — this is exactly how your ad mails." : "Draft preview — your ad shows here now; it prints once our team approves it."}
            </DialogDescription>
          </DialogHeader>
          {!proof ? <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-slate" /></div>
            : proof.slots?.length ? <PostcardCanvas slots={proof.slots} run={proof.run} />
            : <p className="text-sm text-sam-slate py-6 text-center">No card found for this spot yet.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
const PLAN_LABEL = { free: "Free Basic", premium_online: "Premium Online", postcard: "Postcard Advertiser", listing: "Listing", growth: "Growth", pro: "Pro", max: "Max" };
const TERM_LABEL = { per_drop: "Single drop", "4mo": "4-month", "8mo": "8-month", "12mo": "12-month" };
const SIZE_LABEL = { premium: "Premium 4×9", standard: "Standard 4×6", basic: "Basic 4×3" };
const MILESTONES = [100, 50, 10];
const milestoneFor = (n) => MILESTONES.find((m) => n >= m) || 0;

const EMPTY = {
  name: "", parent_category: "", category: "", short_description: "", full_description: "",
  phone: "", website: "", email: "", address_line: "", city: "", state_abbr: "", zip: "",
  hours_mon_fri: "", hours_sat: "", hours_sun: "", price_range: "$$", tags: [], photo_urls: [], products: [],
};

function ShareLeaderboard({ stats }) {
  const { leaderboard = [], trend = [], total = 0 } = stats;
  const maxShare = Math.max(1, ...leaderboard.map((l) => l.shares));
  const maxTrend = Math.max(1, ...trend.map((t) => t.shares));
  const top = leaderboard.filter((l) => l.shares > 0).slice(0, 5);
  return (
    <div className="mb-6 rounded-2xl border border-border bg-card p-5" data-testid="share-leaderboard">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Share2 className="h-4 w-4 text-sam-crimson" />
          <h2 className="font-display text-lg font-bold text-sam-navy">Share Leaderboard</h2>
        </div>
        <span className="text-xs text-sam-slate">{total} total share{total === 1 ? "" : "s"}</span>
      </div>
      {total === 0 ? (
        <p className="text-sm text-sam-slate">No shares yet — tap "Share this business" on any of your listings to start spreading the word.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2.5" data-testid="share-leaderboard-list">
            {top.map((l, i) => (
              <Link key={l.id} to={`/business/${l.slug}`} className="block group">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-semibold text-sam-navy group-hover:text-sam-crimson transition-colors truncate pr-2">
                    {i + 1}. {l.name}
                    {milestoneFor(l.shares) > 0 && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold text-sam-amber align-middle"><Trophy className="h-3 w-3" />{milestoneFor(l.shares)}+</span>
                    )}
                  </span>
                  <span className="font-mono text-sam-navy">{l.shares}</span>
                </div>
                <div className="h-2 rounded-full bg-sam-navy/5 overflow-hidden">
                  <div className="h-full bg-sam-crimson/80 rounded-full transition-all" style={{ width: `${(l.shares / maxShare) * 100}%` }} />
                </div>
              </Link>
            ))}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-sam-slate mb-2">Shares — last 6 months</div>
            <div className="flex items-end justify-between gap-1 h-20" data-testid="share-trend">
              {trend.map((t, i) => (
                <div key={`${t.month}-${i}`} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] font-mono text-sam-navy/70">{t.shares}</span>
                  <div className="w-full rounded-t bg-sam-crimson/70 transition-all" style={{ height: `${Math.max(4, (t.shares / maxTrend) * 56)}px` }} title={`${t.month}: ${t.shares} shares`} />
                  <span className="text-[9px] text-sam-slate">{t.month}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [biz, setBiz] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [cats, setCats] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // business object or "new"
  const [form, setForm] = useState(EMPTY);
  const [agree, setAgree] = useState(false);
  const [upgradeBiz, setUpgradeBiz] = useState(null);
  const [upgradeAgree, setUpgradeAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [trendRange, setTrendRange] = useState(6);
  const [trendMetric, setTrendMetric] = useState("referrals");
  const [referralBiz, setReferralBiz] = useState(null);
  const [referralRows, setReferralRows] = useState([]);
  const [referralLoading, setReferralLoading] = useState(false);
  const [shareStats, setShareStats] = useState(null);
  const [crmOpen, setCrmOpen] = useState(null);
  const [crmData, setCrmData] = useState({});
  const [crmLoading, setCrmLoading] = useState(false);
  const [lsBusy, setLsBusy] = useState(null);

  const connectAdChannels = async (b) => {
    setLsBusy(b.id);
    try {
      const { data } = await api.post(`/my/businesses/${b.id}/lead-source`);
      setCrmData((m) => ({ ...m, [b.id]: { ...(m[b.id] || {}), lead_source_url: data.url } }));
      toast.success("Ad-channel link ready — paste it into your forms & ads");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate link");
    } finally { setLsBusy(null); }
  };

  const toggleCrm = async (b) => {
    if (crmOpen === b.id) { setCrmOpen(null); return; }
    setCrmOpen(b.id);
    if (crmData[b.id]) return;
    setCrmLoading(true);
    try {
      const { data } = await api.get(`/my/businesses/${b.id}/crm`);
      setCrmData((m) => ({ ...m, [b.id]: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your CRM sub-account");
      setCrmOpen(null);
    } finally { setCrmLoading(false); }
  };

  const openReferrals = async (b) => {
    setReferralBiz(b); setReferralLoading(true); setReferralRows([]);
    try { const { data } = await api.get(`/analytics/my/${b.id}/referrals`); setReferralRows(data); }
    catch { setReferralRows([]); }
    finally { setReferralLoading(false); }
  };

  const downloadReferralsCsv = async () => {
    if (!referralBiz) return;
    try {
      const res = await api.get(`/analytics/my/${referralBiz.id}/referrals.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ai-referrals-${referralBiz.slug}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export CSV"); }
  };

  const generateAI = async () => {
    if (!form.name || !form.category) return toast.error("Add a name and category first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-ad", {
        business_id: editing !== "new" ? editing.id : null,
        name: form.name, category: form.category, city: form.city, state_abbr: form.state_abbr, tags: form.tags,
      });
      setForm((f) => ({ ...f,
        short_description: data.short_description || f.short_description,
        full_description: data.full_description || f.full_description,
        _ai_deal_headline: data.deal_headline, _ai_deal_details: data.deal_details }));
      toast.success("AI copy added — review & tweak as needed");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setAiBusy(false); }
  };

  const load = useCallback(() => Promise.all([
    api.get("/my/businesses").then(({ data }) => setBiz(data)),
    api.get("/analytics/my").then(({ data }) => setAnalytics(Object.fromEntries(data.map((a) => [a.id, a])))).catch(() => {}),
    api.get("/analytics/my/shares").then(({ data }) => setShareStats(data)).catch(() => {}),
  ]).finally(() => setLoading(false)), []);
  useEffect(() => {
    load();
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/states").then(({ data }) => setStates(data));
  }, [load]);

  // Celebrate share milestones (once per browser per new level reached)
  const shareListing = useCallback(async (l) => {
    const url = `${window.location.origin}/business/${l.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: l.name, text: `${l.name} just hit ${l.shares} shares on Saving America Money!`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
      api.post(`/businesses/${l.id}/share`).catch(() => {});
    } catch (e) {
      if (e && e.name === "AbortError") return;
      try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); } catch { toast.error("Couldn't copy the link — please copy it from the address bar."); }
    }
  }, []);

  useEffect(() => {
    if (!shareStats) return;
    shareStats.leaderboard.forEach((l) => {
      const level = milestoneFor(l.shares);
      if (!level) return;
      const key = `sam_ms_${l.id}`;
      const prev = Number(localStorage.getItem(key) || 0);
      if (level > prev) {
        localStorage.setItem(key, String(level));
        toast.success(`${l.name} reached ${level} shares!`, {
          description: "Your neighbors are spreading the word — celebrate it publicly.",
          action: { label: "Re-share", onClick: () => shareListing(l) },
        });
      }
    });
  }, [shareStats, shareListing]);

  const openNew = () => { setForm(EMPTY); setAgree(false); setEditing("new"); };
  const openEdit = (b) => {
    setForm({ ...EMPTY, ...b, tags: b.tags || [], photo_urls: b.photo_urls || [],
      products: (b.products || []).map((p) => ({ ...p, _k: Math.random().toString(36).slice(2) })) });
    setEditing(b);
  };

  const parentObj = cats.find((c) => c.slug === form.parent_slug) || cats.find((c) => c.name === form.parent_category);

  const save = async () => {
    if (!form.name || !form.parent_category || !form.category) return toast.error("Name and category are required");
    if (editing === "new" && !agree) return toast.error("Please accept the Terms of Service to create a listing");
    setBusy(true);
    try {
      const { _ai_deal_headline, _ai_deal_details, ...clean } = form;
      const payload = { ...clean, tags: form.tags, photo_urls: form.photo_urls.filter(Boolean),
        products: form.products.map(({ _k, ...p }) => p), terms_accepted: true, terms_version: TERMS_VERSION };
      if (editing === "new") await api.post("/businesses", payload);
      else await api.put(`/businesses/${editing.id}`, payload);
      toast.success(editing === "new" ? "Listing submitted for approval" : "Listing updated (re-submitted for approval)");
      setEditing(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const upgrade = async () => {
    if (!upgradeBiz) return;
    if (!upgradeAgree) return toast.error("Please accept the Terms of Service to continue");
    try {
      const { data } = await api.post("/payments/online-upgrade", { business_id: upgradeBiz.id, origin_url: window.location.origin, affiliate_id: window.affiliateId || undefined, terms_accepted: true, terms_version: TERMS_VERSION });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start upgrade"); }
  };

  const resendInvite = async (b) => {
    try {
      const { data } = await api.post(`/my/businesses/${b.id}/resend-invite`);
      if (data.status === "already_pending") toast.info(`An invite is already pending for ${data.email} — ask them to check spam/promotions.`);
      else if (data.ok) toast.success(`CRM invite re-sent to ${data.email}`);
      else toast.error("Couldn't resend — the invite may already be accepted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend the invite"); }
  };

  const publishListing = async (b) => {
    try { await api.post(`/my/businesses/${b.id}/publish`); toast.success("Listing published"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
  };

  const setProduct = (i, key, val) => {
    const products = [...form.products]; products[i] = { ...products[i], [key]: val }; setForm({ ...form, products });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-sam-navy">My Business Dashboard</h1>
          <p className="text-sam-slate mt-1">Welcome back, {user?.name?.split(" ")[0]} · {user?.role}</p>
        </div>
        <Button className="bg-sam-crimson hover:bg-sam-crimson/90" onClick={openNew} data-testid="add-business-btn"><Plus className="h-4 w-4 mr-2" />Add Business</Button>
      </div>

      <TermsReconsentBanner />

      <PartnerKit />

      <GrowPanel />

      {user?.role === "owner_staff" && (() => {
        const L = { edit_listing: "Edit the listing", view_leads: "View leads", manage_billing: "Manage billing" };
        const ps = user.owner_perms || [];
        return (
          <div className="mb-6 rounded-xl border border-sam-navy/15 bg-sam-navy/5 px-4 py-3" data-testid="owner-staff-banner">
            <p className="text-sm text-sam-navy"><span className="font-semibold">You're helping manage this business.</span> You can: {ps.length ? ps.map((p) => L[p] || p).join(" · ") : "no areas yet — ask the owner to grant access."}</p>
          </div>
        );
      })()}

      {!loading && biz.length > 0 && shareStats && <ShareLeaderboard stats={shareStats} />}

      {loading ? <div className="h-48 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>
        : biz.length === 0 ? (
          <div className="grid gap-4">
            {user?.role === "owner" && <OwnerTeam />}
            <div className="rounded-2xl border border-dashed p-12 text-center">
              <Store className="h-12 w-12 text-sam-slate mx-auto mb-3" />
              <p className="font-display text-lg text-sam-navy">You haven't listed a business yet.</p>
              <p className="text-sam-slate text-sm mt-1">Every listing starts free. Upgrade later for products, deals &amp; featured placement.</p>
              <Button className="mt-4 bg-sam-navy" onClick={openNew}>Create my free listing</Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {user?.role === "owner" && <OwnerTeam />}
            {biz.map((b) => (
              <div key={b.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`my-biz-${b.slug}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-xl font-bold text-sam-navy">{b.name}</h3>
                      <Badge className={STATUS_BADGE[b.listing_status]}>{b.listing_status}</Badge>
                      <Badge variant="outline">{PLAN_LABEL[b.listing_plan]}</Badge>
                    </div>
                    <p className="text-sm text-sam-slate mt-0.5">{b.category} · {b.city}, {b.state_abbr}</p>
                    {b.listing_plan === "postcard" && b.mailer_commitment && (() => {
                      const c = b.mailer_commitment;
                      const ms = new Date(c.expires).getTime() - Date.now();
                      const days = Math.max(0, Math.ceil(ms / 86400000));
                      return (
                        <div data-testid={`plan-badge-${b.slug}`} className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                          <span className="inline-flex items-center gap-1 rounded-full bg-sam-crimson/10 text-sam-crimson font-semibold px-2 py-0.5"><Megaphone className="h-3 w-3" />{SIZE_LABEL[c.ad_size] || c.ad_size}</span>
                          <span className="rounded-full bg-sam-navy/10 text-sam-navy font-semibold px-2 py-0.5">{TERM_LABEL[c.term] || c.term} · {c.drops} drop{c.drops > 1 ? "s" : ""}</span>
                          <span className="text-sam-slate">ZIP {c.central_zip}</span>
                          <span className={ms <= 0 ? "text-slate-400 font-semibold" : "text-sam-navy font-semibold"}>{ms <= 0 ? "Commitment ended" : `${days} day${days === 1 ? "" : "s"} left`}</span>
                        </div>
                      );
                    })()}
                    {b.listing_plan === "premium_online" && (
                      <div data-testid={`plan-badge-${b.slug}`} className="mt-1.5 text-xs"><span className="rounded-full bg-sam-navy/10 text-sam-navy font-semibold px-2 py-0.5">$29/mo online listing</span></div>
                    )}
                    {b.zuops_workspace_id && (
                      <div data-testid={`crm-badge-${b.slug}`} className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 rounded-full bg-sam-green/10 text-sam-green font-semibold px-2 py-0.5"><Sparkles className="h-3 w-3" />CRM sub-account active</span>
                        <button type="button" onClick={() => toggleCrm(b)} data-testid={`crm-toggle-${b.slug}`} className="inline-flex items-center gap-1 text-sam-navy font-semibold hover:text-sam-crimson">
                          {crmOpen === b.id ? "Hide" : "View leads & number"}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${crmOpen === b.id ? "rotate-180" : ""}`} />
                        </button>
                        <button type="button" onClick={() => resendInvite(b)} data-testid={`crm-resend-${b.slug}`} className="text-sam-crimson font-semibold hover:underline">Resend invite</button>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {b.listing_status === "approved" && <Button variant="outline" size="sm" asChild><Link to={`/business/${b.slug}`}>View</Link></Button>}
                    {b.listing_status === "draft" && <Button size="sm" className="bg-sam-green hover:bg-sam-green/90" onClick={() => publishListing(b)} data-testid={`publish-${b.slug}`}>Publish</Button>}
                    <Button variant="outline" size="sm" asChild data-testid={`refer-${b.slug}`}>
                      <Link to={`/refer?zip=${encodeURIComponent(b.zip || "")}&city=${encodeURIComponent(b.city || "")}&state=${encodeURIComponent(b.state_abbr || "")}&ref=${encodeURIComponent(b.name || "")}`}>
                        <Users className="h-3.5 w-3.5 mr-1" />Refer businesses
                      </Link>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`edit-${b.slug}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                  </div>
                </div>
                <SamLeads business={b} />
                {b.listing_plan === "postcard" && <AdProof business={b} />}
                <LeadStats business={b} />
                <CreditMeter business={b} />
                {(() => {
                  if (b.listing_plan !== "postcard" || !b.postcard_deal_expires) return null;
                  const ms = new Date(b.postcard_deal_expires).getTime() - Date.now();
                  if (ms > 7 * 86400000) return null;
                  const expired = ms <= 0;
                  const days = Math.ceil(ms / 86400000);
                  return (
                    <div data-testid={`renew-nudge-${b.slug}`} className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${expired ? "border-slate-300 bg-slate-50" : "border-sam-amber/50 bg-sam-amber/10"}`}>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className={`h-4 w-4 shrink-0 ${expired ? "text-slate-400" : "text-sam-amber"}`} />
                        <span className="text-sam-navy">
                          <span className="font-semibold">{expired ? "Your postcard deal has expired" : `Your postcard deal ends ${days <= 1 ? "today" : `in ${days} days`}`}</span>
                          {b.postcard_deal_headline ? ` — "${b.postcard_deal_headline}"` : ""}
                        </span>
                      </div>
                      <Button size="sm" className="bg-sam-crimson hover:bg-sam-crimson/90" asChild data-testid={`renew-deal-${b.slug}`}>
                        <Link to="/postcards">{expired ? "Renew deal" : "Renew now"}</Link>
                      </Button>
                    </div>
                  );
                })()}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid={`analytics-${b.slug}`}>
                  {[
                    { Icon: Eye, label: "Views", val: analytics[b.id]?.views ?? 0 },
                    { Icon: Share2, label: "Shares", val: analytics[b.id]?.shares ?? 0 },
                    { Icon: Heart, label: "Saves", val: analytics[b.id]?.saves ?? 0 },
                    { Icon: Ticket, label: "Redemptions", val: analytics[b.id]?.redemptions ?? 0 },
                    { Icon: Star, label: "Reviews", val: analytics[b.id]?.reviews ?? 0 },
                    { Icon: Megaphone, label: "Live Ads", val: analytics[b.id]?.active_slots ?? 0 },
                    { Icon: Sparkles, label: "AI Sent", val: analytics[b.id]?.ai_referrals ?? 0,
                      onClick: (analytics[b.id]?.ai_referrals ?? 0) > 0 ? () => openReferrals(b) : undefined,
                      testid: `ai-sent-card-${b.slug}` },
                  ].map((m) => (
                    <div key={m.label} data-testid={m.testid} onClick={m.onClick} role={m.onClick ? "button" : undefined}
                      className={`rounded-lg bg-sam-navy/5 p-2.5 text-center ${m.onClick ? "cursor-pointer hover:bg-sam-crimson/10 ring-1 ring-sam-crimson/20 transition-colors" : ""}`}>
                      <m.Icon className="h-4 w-4 mx-auto text-sam-crimson" />
                      <div className="font-mono text-lg font-bold text-sam-navy mt-0.5">{m.val}</div>
                      <div className="text-[10px] text-sam-slate uppercase tracking-wide">{m.label}{m.onClick ? " ›" : ""}</div>
                    </div>
                  ))}
                </div>
                {(analytics[b.id]?.ai_referral_trend?.some((t) => (t.referrals || t.views || t.saves))) && (() => {
                  const full = analytics[b.id].ai_referral_trend;
                  const trend = full.slice(-trendRange);
                  const METRICS = { referrals: "AI referrals", views: "Views", saves: "Saves" };
                  const max = Math.max(1, ...trend.map((t) => t[trendMetric]));
                  return (
                    <div className="mt-3 rounded-lg bg-sam-navy/5 p-3" data-testid={`ai-trend-${b.slug}`}>
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-sam-slate flex items-center gap-1"><Sparkles className="h-3 w-3 text-sam-crimson" />{METRICS[trendMetric]} — last {trendRange} months</div>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1" data-testid={`trend-metric-${b.slug}`}>
                            {Object.entries(METRICS).map(([k, label]) => (
                              <button key={k} type="button" onClick={() => setTrendMetric(k)} data-testid={`trend-metric-${k}-${b.slug}`}
                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded border transition-colors ${trendMetric === k ? "bg-sam-crimson text-white border-sam-crimson" : "border-sam-navy/20 text-sam-slate hover:border-sam-crimson/50"}`}>{label}</button>
                            ))}
                          </div>
                          <div className="flex gap-1" data-testid={`trend-range-${b.slug}`}>
                            {[6, 12].map((r) => (
                              <button key={r} type="button" onClick={() => setTrendRange(r)} data-testid={`trend-range-${r}-${b.slug}`}
                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded border transition-colors ${trendRange === r ? "bg-sam-navy text-white border-sam-navy" : "border-sam-navy/20 text-sam-slate hover:border-sam-navy/50"}`}>{r}M</button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-end justify-between gap-1 h-16">
                        {trend.map((t, i) => (
                          <div key={`${t.month}-${i}`} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-[9px] font-mono text-sam-navy/70">{t[trendMetric]}</span>
                            <div className={`w-full rounded-t transition-all cursor-help ${trendMetric === "referrals" ? "bg-sam-crimson/80" : trendMetric === "views" ? "bg-sam-navy/70" : "bg-sam-amber"}`} style={{ height: `${Math.max(4, (t[trendMetric] / max) * 44)}px` }} title={`${t.month} — AI referrals: ${t.referrals} · Views: ${t.views} · Saves: ${t.saves}`} />
                            <span className="text-[9px] text-sam-slate">{t.month}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {analytics[b.id]?.ab_results && (() => {
                  const ab = analytics[b.id].ab_results;
                  const winner = ab.A.redemptions === ab.B.redemptions ? null : (ab.A.redemptions > ab.B.redemptions ? "A" : "B");
                  const hi = winner ? ab[winner].redemptions : 0;
                  const lo = winner ? ab[winner === "A" ? "B" : "A"].redemptions : 0;
                  let insight;
                  if (!winner) {
                    insight = (ab.A.redemptions + ab.B.redemptions) === 0
                      ? "No QR redemptions yet — both variations are still gathering data."
                      : `It's a dead heat at ${ab.A.redemptions} each — keep both running to break the tie.`;
                  } else if (lo === 0) {
                    insight = `Variation ${winner} is the only one drawing scans (${hi}) — reuse its headline on your next card.`;
                  } else {
                    const mult = hi / lo;
                    const by = mult >= 2 ? `${mult.toFixed(mult % 1 ? 1 : 0)}×` : `${hi - lo} more scan${hi - lo === 1 ? "" : "s"}`;
                    insight = `Variation ${winner} won by ${by} — reuse its headline on your next card.`;
                  }
                  return (
                    <div className="mt-3 rounded-lg bg-sam-navy/5 p-3" data-testid={`ab-results-${b.slug}`}>
                      <div className="text-[10px] font-bold uppercase tracking-wide text-sam-slate mb-2">Postcard A/B test — QR redemptions</div>
                      <div className="grid grid-cols-2 gap-2">
                        {["A", "B"].map((k) => (
                          <div key={k} className={`rounded-lg p-2.5 text-center border ${winner === k ? "border-sam-crimson bg-sam-crimson/10" : "border-border bg-white"}`}>
                            <div className="text-xs font-bold text-sam-navy">Variation {k} {winner === k && <span className="text-sam-crimson">· Winner</span>}</div>
                            <div className="font-mono text-xl font-bold text-sam-navy">{ab[k].redemptions}</div>
                            <div className="text-[10px] text-sam-slate">{ab[k].count} ad{ab[k].count === 1 ? "" : "s"} live</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-sam-navy font-semibold" data-testid={`ab-insight-${b.slug}`}>
                        <Sparkles className="h-3.5 w-3.5 text-sam-crimson shrink-0 mt-0.5" />{insight}
                      </div>
                    </div>
                  );
                })()}
                {crmOpen === b.id && (
                  <div className="mt-4 rounded-xl border border-sam-green/30 bg-sam-green/[0.04] p-4" data-testid={`crm-panel-${b.slug}`}>
                    {crmLoading && !crmData[b.id] ? (
                      <div className="h-20 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-sam-green" /></div>
                    ) : crmData[b.id] ? (() => {
                      const d = crmData[b.id];
                      return (
                        <div className="space-y-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-border px-3 py-1.5 text-sm font-semibold text-sam-navy" data-testid={`crm-phone-${b.slug}`}>
                              <Phone className="h-4 w-4 text-sam-green" />
                              {d.phone_numbers?.length ? d.phone_numbers.join(", ") : "No number yet"}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-border px-3 py-1.5 text-sm font-semibold text-sam-navy">
                              <Inbox className="h-4 w-4 text-sam-crimson" />{d.lead_count} lead{d.lead_count === 1 ? "" : "s"}
                            </span>
                            {d.forms?.map((f) => (
                              <span key={f.slug} data-testid={`crm-form-${b.slug}`} className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-border px-3 py-1.5 text-sm font-semibold text-sam-navy"><FileText className="h-4 w-4 text-sam-navy" />{f.name || "Quote form"}{!f.published && <span className="text-[10px] text-sam-slate font-normal">(draft)</span>}</span>
                            ))}
                            {d.calendars?.map((c) => (
                              <span key={c.slug} data-testid={`crm-cal-${b.slug}`} className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-border px-3 py-1.5 text-sm font-semibold text-sam-navy"><CalendarCheck className="h-4 w-4 text-sam-navy" />{c.name || "Booking"}</span>
                            ))}
                          </div>
                          {!d.phone_numbers?.length && (
                            <p className="text-xs text-sam-slate">No phone number yet. Accept your dashboard invite ({d.invite_email}), then add a number &amp; message credits inside your dashboard to turn on calling &amp; texting — all calls, texts, form fills and bookings then track here and in your dashboard.</p>
                          )}
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-sam-slate mb-2">Recent leads in your sub-account</div>
                            {d.leads?.length ? (
                              <div className="space-y-1.5" data-testid={`crm-leads-${b.slug}`}>
                                {d.leads.map((l) => (
                                  <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white border border-border px-3 py-2 text-sm">
                                    <div className="min-w-0">
                                      <span className="font-semibold text-sam-navy">{l.name}</span>
                                      {l.email && <a href={`mailto:${l.email}`} className="ml-2 text-sam-slate hover:text-sam-crimson">{l.email}</a>}
                                      {l.phone && <a href={`tel:${l.phone}`} className="ml-2 text-sam-slate hover:text-sam-crimson">{l.phone}</a>}
                                    </div>
                                    <Badge variant="outline" className="capitalize">{l.status}</Badge>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-sam-slate">No leads in your dashboard yet. Share your quote form &amp; booking link, or connect your ad channels in your dashboard — new leads will appear here.</p>
                            )}
                          </div>
                          <div className="rounded-lg border border-sam-navy/15 bg-white p-3" data-testid={`crm-connect-${b.slug}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-sam-navy flex items-center gap-1.5"><Link2 className="h-4 w-4 text-sam-crimson" />Connect my ad channels</div>
                              {!d.lead_source_url && (
                                <Button size="sm" variant="outline" onClick={() => connectAdChannels(b)} disabled={lsBusy === b.id} data-testid={`crm-connect-btn-${b.slug}`}>
                                  {lsBusy === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Generate link"}
                                </Button>
                              )}
                            </div>
                            {d.lead_source_url ? (
                              <>
                                <div className="mt-2 flex items-center gap-2">
                                  <input readOnly value={d.lead_source_url} onFocus={(e) => e.target.select()} data-testid={`crm-ls-url-${b.slug}`} className="flex-1 min-w-0 text-xs font-mono bg-sam-navy/5 rounded px-2 py-1.5 truncate" />
                                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(d.lead_source_url); toast.success("Link copied"); }} data-testid={`crm-ls-copy-${b.slug}`}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
                                </div>
                                <p className="text-[11px] text-sam-slate mt-1.5">Paste this into your website form, Facebook/Google lead ads, or any tool — leads POST straight into your sub-account. Treat it like a secret: anyone with it can add leads.</p>
                                <details className="mt-2 group" data-testid={`crm-fb-guide-${b.slug}`}>
                                  <summary className="cursor-pointer text-xs font-semibold text-sam-crimson select-none">How to connect Facebook Lead Ads</summary>
                                  <div className="mt-2 rounded-md bg-sam-navy/5 p-2.5 text-[11px] text-sam-slate space-y-1.5">
                                    <ol className="list-decimal ml-4 space-y-1">
                                      <li>In <span className="font-semibold text-sam-navy">Zapier</span> (or Make), create a Zap: trigger <span className="font-semibold text-sam-navy">Facebook Lead Ads → New Lead</span>, pick your Page &amp; form.</li>
                                      <li>Add action <span className="font-semibold text-sam-navy">Webhooks → POST</span>. Set URL to the link above and Payload Type to <span className="font-mono">JSON</span>.</li>
                                      <li>Map your Facebook fields to these exact keys:</li>
                                    </ol>
                                    <div className="rounded bg-white border border-border overflow-hidden">
                                      {[["Full name", "full_name"], ["Email", "email"], ["Phone number", "phone"], ["Zip / Postal code", "zip"], ["City/Street", "address"], ["Question / message", "inquiry"]].map(([fb, key]) => (
                                        <div key={key} className="flex items-center justify-between px-2 py-1 border-b border-border last:border-0">
                                          <span>{fb}</span><span className="font-mono text-sam-navy">{key}</span>
                                        </div>
                                      ))}
                                    </div>
                                    <p>Turn the Zap on — new Facebook leads now land in this sub-account instantly. (Same steps work for Google Lead Form extensions.)</p>
                                  </div>
                                </details>
                              </>
                            ) : (
                              <p className="text-xs text-sam-slate mt-1">Generate a private URL that sends leads from your website forms and ad platforms directly into this dashboard, so every lead tracks here in one place.</p>
                            )}
                          </div>
                        </div>
                      );
                    })() : null}
                  </div>
                )}
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {b.listing_plan !== "premium_online" && (
                    <div className="rounded-xl bg-sam-amber/10 border border-sam-amber/30 p-3 flex items-center justify-between gap-3">
                      <div className="text-sm"><div className="font-bold text-sam-navy flex items-center gap-1"><Sparkles className="h-4 w-4 text-sam-amber" />Online Upgrade</div><div className="text-sam-slate">Products, deals & featured — {money(29)}/mo</div></div>
                      <Button size="sm" className="bg-sam-amber hover:bg-sam-amber/90" onClick={() => { setUpgradeBiz(b); setUpgradeAgree(false); }} data-testid={`upgrade-${b.slug}`}>Upgrade</Button>
                    </div>
                  )}
                  <div className="rounded-xl bg-sam-crimson/5 border border-sam-crimson/20 p-3 flex items-center justify-between gap-3">
                    <div className="text-sm"><div className="font-bold text-sam-navy flex items-center gap-1"><Ticket className="h-4 w-4 text-sam-crimson" />Postcard Mailer</div><div className="text-sam-slate">Advertise to 10,000 homes</div></div>
                    <Button size="sm" variant="outline" asChild><Link to="/postcards">Book slot</Link></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="business-editor">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "new" ? "Add your business" : "Edit listing"}</DialogTitle>
            <DialogDescription>Fill in your business details. New listings and edits are submitted for admin approval before going live.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="basics">
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="basics">Basics</TabsTrigger>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="premium" disabled={editing !== "new" && editing?.listing_plan !== "premium_online"}>Products</TabsTrigger>
            </TabsList>

            <TabsContent value="basics" className="space-y-3 pt-3">
              <div className="rounded-lg border border-sam-amber/40 bg-sam-amber/5 p-3 flex items-center justify-between gap-3">
                <div className="text-xs text-sam-slate">Let Claude write your tagline, description & a postcard deal from your name, category & tags.</div>
                <Button type="button" size="sm" className="bg-sam-navy hover:bg-sam-navy2 shrink-0" onClick={generateAI} disabled={aiBusy} data-testid="ai-generate-btn">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />Generate with AI</>}
                </Button>
              </div>
              {form._ai_deal_headline && (
                <div className="rounded-lg bg-sam-crimson/5 border border-sam-crimson/20 p-2.5 text-xs">
                  <span className="font-bold text-sam-crimson">Suggested postcard deal:</span> <span className="text-sam-navy font-semibold">{form._ai_deal_headline}</span>
                  <span className="text-sam-slate"> — saved to your deal library; pick it when you reserve a postcard slot.</span>
                </div>
              )}
              <div><Label>Business name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="biz-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category *</Label>
                  <Select value={form.parent_category} onValueChange={(v) => setForm({ ...form, parent_category: v, parent_slug: cats.find((c) => c.name === v)?.slug, category: "" })}>
                    <SelectTrigger data-testid="biz-parent"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-64">{cats.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Subcategory *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} disabled={!parentObj}>
                    <SelectTrigger data-testid="biz-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-64">{parentObj?.children.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Short description</Label><Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} maxLength={160} data-testid="biz-short" /></div>
              <div><Label>Full description</Label><Textarea value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} /></div>
            </TabsContent>

            <TabsContent value="details" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
              </div>
              <div><Label>Address</Label><Input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="biz-city" /></div>
                <div><Label>State</Label>
                  <Select value={form.state_abbr} onValueChange={(v) => setForm({ ...form, state_abbr: v })}>
                    <SelectTrigger data-testid="biz-state"><SelectValue placeholder="ST" /></SelectTrigger>
                    <SelectContent className="max-h-64">{states.map((s) => <SelectItem key={s.abbr} value={s.abbr}>{s.abbr}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>ZIP</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price range</Label>
                  <Select value={form.price_range} onValueChange={(v) => setForm({ ...form, price_range: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["$", "$$", "$$$", "$$$$"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Photo URL</Label><Input value={form.photo_urls[0] || ""} onChange={(e) => setForm({ ...form, photo_urls: [e.target.value] })} placeholder="https://…" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Hours Mon–Fri</Label><Input value={form.hours_mon_fri} onChange={(e) => setForm({ ...form, hours_mon_fri: e.target.value })} /></div>
                <div><Label>Sat</Label><Input value={form.hours_sat} onChange={(e) => setForm({ ...form, hours_sat: e.target.value })} /></div>
                <div><Label>Sun</Label><Input value={form.hours_sun} onChange={(e) => setForm({ ...form, hours_sun: e.target.value })} /></div>
              </div>
              <div><Label>Tags (comma separated)</Label><Input value={form.tags.join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} placeholder="Free Estimates, Licensed, Family-Owned" /></div>
            </TabsContent>

            <TabsContent value="premium" className="space-y-3 pt-3">
              <p className="text-sm text-sam-slate">Premium listings can showcase products & services.</p>
              {form.products.map((p, i) => (
                <div key={p._k || `prod-${i}`} className="grid grid-cols-[1fr_100px_auto] gap-2 items-end">
                  <div><Label className="text-xs">Name</Label><Input value={p.name || ""} onChange={(e) => setProduct(i, "name", e.target.value)} /></div>
                  <div><Label className="text-xs">Price</Label><Input value={p.price || ""} onChange={(e) => setProduct(i, "price", e.target.value)} /></div>
                  <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, products: form.products.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setForm({ ...form, products: [...form.products, { name: "", price: "", desc: "", _k: Math.random().toString(36).slice(2) }] })}><Plus className="h-4 w-4 mr-1" />Add product</Button>
            </TabsContent>
          </Tabs>
          {editing === "new" && <div className="mt-2"><TermsAgree checked={agree} onChange={setAgree} testid="new-listing-terms" /></div>}
          <Button className="w-full bg-sam-navy hover:bg-sam-navy2 mt-2" onClick={save} disabled={busy || (editing === "new" && !agree)} data-testid="save-business-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{editing === "new" ? "Submit for approval" : "Save changes"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Online upgrade confirm */}
      <Dialog open={!!upgradeBiz} onOpenChange={(o) => { if (!o) { setUpgradeBiz(null); setUpgradeAgree(false); } }}>
        <DialogContent data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Upgrade to the $29/mo online plan</DialogTitle>
            <DialogDescription>Unlock products, deals & featured placement for {upgradeBiz?.name}. Billed monthly, cancel anytime.</DialogDescription>
          </DialogHeader>
          <TermsAgree checked={upgradeAgree} onChange={setUpgradeAgree} testid="upgrade-terms" />
          <Button className="w-full bg-sam-amber hover:bg-sam-amber/90 mt-1" onClick={upgrade} disabled={!upgradeAgree} data-testid="upgrade-confirm-btn">Continue to secure checkout</Button>
        </DialogContent>
      </Dialog>

      {/* AI referral sources */}
      <Dialog open={!!referralBiz} onOpenChange={(o) => !o && setReferralBiz(null)}>
        <DialogContent className="max-w-md" data-testid="referral-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-sam-crimson" />Searches that sent shoppers your way</DialogTitle>
            <DialogDescription>Shopper searches the SAM Assistant matched to this listing this month.</DialogDescription>
          </DialogHeader>
          {referralRows.length > 0 && (
            <Button variant="outline" size="sm" className="self-start border-sam-navy/30" onClick={() => downloadReferralsCsv()} data-testid="referral-export-btn">
              <Download className="h-4 w-4 mr-1.5" />Export CSV
            </Button>
          )}
          <p className="text-sm text-sam-slate -mt-2">{referralBiz?.name} · this month</p>
          <div className="max-h-[50vh] overflow-y-auto space-y-2 mt-2">
            {referralLoading ? (
              <div className="h-24 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-sam-crimson" /></div>
            ) : referralRows.length === 0 ? (
              <p className="text-sm text-sam-slate text-center py-6">No assistant referrals yet this month.</p>
            ) : referralRows.map((r, i) => (
              <div key={`${r.session_id || "r"}-${r.created_at}-${i}`} className="rounded-lg border border-border bg-card px-3 py-2" data-testid={`referral-row-${i}`}>
                <div className="text-sm font-semibold text-sam-navy">“{r.query}”</div>
                <div className="text-[11px] text-sam-slate">{new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Sparkline({ data }) {
  const vals = data.map((d) => d.credits || 0);
  const max = Math.max(1, ...vals);
  return (
    <div className="flex items-end gap-0.5 h-9" title="Daily marketing-credit usage">
      {vals.map((v, i) => {
        const hpct = v > 0 ? Math.max(8, (v / max) * 100) : 4;
        const hot = max > 0 && v >= max * 0.9 && v > 0;
        return <div key={i} title={`${data[i].date}: ${Math.round(v)} credits`} className={`w-1.5 rounded-sm ${hot ? "bg-sam-crimson" : "bg-sam-navy/50"}`} style={{ height: `${hpct}%` }} data-testid={`spark-bar-${i}`} />;
      })}
    </div>
  );
}

function CreditMeter({ business }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!business.zuops_workspace_id) return;
    api.get(`/my/businesses/${business.id}/credits`).then((r) => setD(r.data)).catch(() => {});
  }, [business.id, business.zuops_workspace_id]);
  useEffect(() => { load(); }, [load]);
  if (!business.zuops_workspace_id || !d || !d.enabled) return null;
  const pct = Math.min(100, Math.round((d.pct || 0) * 100));
  const used = Math.round(d.total_credits || 0);
  const allow = d.allowance || 0;
  const paused = d.status === "paused";
  const barColor = pct >= 100 ? "bg-sam-crimson" : pct >= 80 ? "bg-sam-amber" : "bg-sam-green";
  const TOOL_LABEL = { sms: "Text messages", missed_call_text: "Missed-call texts", ai: "AI assistant", ai_receptionist: "AI receptionist", nurture: "Nurture campaigns", auto_greet: "Auto-greetings", calls: "Calls", email: "Emails" };
  const byTool = Object.entries(d.by_tool || {}).filter(([, v]) => Number(v) > 0).sort((a, b) => b[1] - a[1]);
  const buy = async (pack) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plans/credit-pack/checkout`, { pack, business_id: business.id, origin_url: window.location.origin, affiliate_id: window.affiliateId || undefined });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };
  const savePref = async (patch) => {
    try { const { data } = await api.patch(`/my/businesses/${business.id}/credit-prefs`, patch); setD({ ...d, ...data }); toast.success("Saved"); }
    catch { toast.error("Could not save"); }
  };
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`credits-${business.slug}`}>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="font-semibold text-sam-navy inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-sam-amber" />Marketing credits this month</span>
        <span className="text-sam-slate" data-testid={`credits-usage-${business.slug}`}>{used} of {allow} used</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} /></div>
      {paused
        ? <p className="mt-1.5 text-xs text-sam-crimson font-semibold" data-testid={`credits-paused-${business.slug}`}>Credits used up — automated texting, calling &amp; AI follow-up are paused. New leads are still captured. Add a top-up or upgrade to resume instantly.</p>
        : pct >= 80 ? <p className="mt-1.5 text-xs text-sam-amber font-semibold">You're running low on marketing credits for this period.</p> : null}
      {byTool.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-sam-slate" data-testid={`credits-breakdown-${business.slug}`}>
          <span className="font-semibold text-sam-navy">Where your credits went:</span>
          {byTool.map(([k, v]) => (
            <span key={k} data-testid={`credits-tool-${business.slug}-${k}`}>{TOOL_LABEL[k] || k} <b className="text-sam-navy">{Math.round(v)}</b></span>
          ))}
        </div>
      )}
      {Array.isArray(d.daily) && d.daily.length >= 2 && (
        <div className="mt-2" data-testid={`credits-sparkline-${business.slug}`}>
          <div className="text-xs font-semibold text-sam-navy mb-1">Daily usage trend</div>
          <Sparkline data={d.daily} />
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => buy("100")} data-testid={`credits-buy-100-${business.slug}`}>Add 100 credits · $49</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => buy("500")} data-testid={`credits-buy-500-${business.slug}`}>Add 500 credits · $199</Button>
      </div>
      <div className="mt-2.5 flex flex-col gap-1.5 text-xs">
        <label className="inline-flex items-center gap-2 text-sam-navy" data-testid={`credits-autotopup-${business.slug}`}>
          <input type="checkbox" className="accent-sam-crimson" checked={!!d.auto_topup} onChange={(e) => savePref({ auto_topup: e.target.checked, auto_topup_pack: d.auto_topup_pack || "100" })} data-testid={`credits-autotopup-toggle-${business.slug}`} />
          Auto top-up when I run out
          {d.auto_topup && (
            <select value={d.auto_topup_pack || "100"} onChange={(e) => savePref({ auto_topup_pack: e.target.value })} className="ml-1 rounded border border-border px-1.5 py-0.5 text-xs" data-testid={`credits-autotopup-pack-${business.slug}`}>
              <option value="100">100 credits · $49</option>
              <option value="500">500 credits · $199</option>
            </select>
          )}
        </label>
        <label className="inline-flex items-center gap-2 text-sam-navy" data-testid={`credits-smsalerts-${business.slug}`}>
          <input type="checkbox" className="accent-sam-crimson" checked={!!d.sms_alerts} onChange={(e) => savePref({ sms_alerts: e.target.checked })} disabled={!d.has_phone} data-testid={`credits-smsalerts-toggle-${business.slug}`} />
          Text me when credits run low {!d.has_phone && <span className="text-sam-slate">(add a phone to your listing first)</span>}
        </label>
      </div>
    </div>
  );
}

function OwnerTeam() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [perms, setPerms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [acts, setActs] = useState(null);
  const [actFilterPerson, setActFilterPerson] = useState("");
  const [actFilterAction, setActFilterAction] = useState("");
  const [actSkip, setActSkip] = useState(0);
  const AREAS = [["edit_listing", "Edit listing"], ["view_leads", "View leads"], ["manage_billing", "Manage billing"]];
  const load = () => api.get("/my/team").then(({ data }) => setD(data)).catch(() => {});
  const loadActs = (skip = 0) => {
    const params = new URLSearchParams({ skip: String(skip), limit: "20" });
    if (actFilterPerson) params.set("actor", actFilterPerson);
    if (actFilterAction) params.set("action", actFilterAction);
    api.get(`/my/activity?${params.toString()}`).then(({ data }) => {
      setActs((prev) => ({ ...data, items: skip > 0 ? [...(prev?.items || []), ...data.items] : data.items }));
      setActSkip(skip);
    }).catch(() => setActs({ items: [], total: 0, has_more: false, actors: [], actions: [] }));
  };
  useEffect(() => { if (open && !d) load(); }, [open, d]);
  useEffect(() => { if (open) loadActs(0); /* eslint-disable-next-line */ }, [open, actFilterPerson, actFilterAction]);
  const toggle = (a) => setPerms((p) => p.includes(a) ? p.filter((x) => x !== a) : [...p, a]);
  const invite = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    setBusy(true);
    try { await api.post("/my/team/invite", { email: email.trim(), name: name.trim(), perms }); toast.success("Invite sent"); setEmail(""); setName(""); setPerms([]); load(); loadActs(0); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not invite"); } finally { setBusy(false); }
  };
  const savePerms = async (uid, p) => { try { await api.patch(`/my/team/${uid}`, { perms: p }); load(); } catch { toast.error("Failed"); } };
  const remove = async (uid) => { try { await api.delete(`/my/team/${uid}`); toast.success("Removed"); load(); loadActs(0); } catch { toast.error("Failed"); } };
  const prettyAction = (a) => (a || "").replace(/_/g, " ").replace("businesses", "listing").replace("credit", "billing");
  return (
    <div className="mb-6 rounded-2xl border border-border bg-card p-5" data-testid="owner-team">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between" data-testid="owner-team-toggle">
        <span className="font-display font-bold text-sam-navy inline-flex items-center gap-2"><Users className="h-4 w-4 text-sam-crimson" />Team &amp; staff access</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="w-36" data-testid="owner-invite-name" /></div>
            <div><Label className="text-xs">Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="w-56" placeholder="staff@email.com" data-testid="owner-invite-email" /></div>
            <Button onClick={invite} disabled={busy} data-testid="owner-invite-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invite"}</Button>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            {AREAS.map(([a, label]) => (
              <label key={a} className="inline-flex items-center gap-1.5" data-testid={`owner-invite-perm-${a}`}>
                <input type="checkbox" className="accent-sam-crimson" checked={perms.includes(a)} onChange={() => toggle(a)} />{label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-sam-slate">They'll get an email link to set their own password and can only use the areas you check.</p>
          {d && d.members.length > 0 && (
            <div className="space-y-3 border-t border-border pt-3">
              {d.members.map((m) => (
                <div key={m.user_id} data-testid={`owner-member-${m.user_id}`} className="border-b border-border/60 pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm"><b className="text-sam-navy">{m.name}</b> {m.email} <span className={`text-xs ${m.status === "invited" ? "text-sam-amber" : "text-sam-green"}`}>({m.status})</span></span>
                    <Button size="sm" variant="outline" className="text-sam-crimson" onClick={() => remove(m.user_id)} data-testid={`owner-remove-${m.user_id}`}>Remove</Button>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs mt-1.5">
                    {AREAS.map(([a, label]) => (
                      <label key={a} className="inline-flex items-center gap-1" data-testid={`owner-perm-${m.user_id}-${a}`}>
                        <input type="checkbox" className="accent-sam-crimson" checked={(m.perms || []).includes(a)}
                          onChange={() => savePerms(m.user_id, (m.perms || []).includes(a) ? m.perms.filter((x) => x !== a) : [...(m.perms || []), a])} />{label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-border pt-3" data-testid="owner-activity">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <h4 className="font-display font-bold text-sam-navy text-sm">Recent staff activity</h4>
              <div className="flex gap-2">
                <select value={actFilterPerson} onChange={(e) => setActFilterPerson(e.target.value)} className="text-xs border border-border rounded-md px-2 py-1 bg-background" data-testid="owner-activity-filter-person">
                  <option value="">All people</option>
                  {(acts?.actors || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select value={actFilterAction} onChange={(e) => setActFilterAction(e.target.value)} className="text-xs border border-border rounded-md px-2 py-1 bg-background" data-testid="owner-activity-filter-action">
                  <option value="">All actions</option>
                  {(acts?.actions || []).map((a) => <option key={a} value={a}>{prettyAction(a)}</option>)}
                </select>
              </div>
            </div>
            {!acts ? <Loader2 className="h-4 w-4 animate-spin text-sam-slate" /> : acts.items.length === 0 ? (
              <p className="text-xs text-sam-slate">No staff activity yet.</p>
            ) : (
              <div className="space-y-1.5 text-xs">
                {acts.items.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 border-b border-border/50 pb-1" data-testid={`owner-activity-${a.id}`}>
                    <span className="text-sam-navy"><b>{a.name || a.email}</b> {prettyAction(a.action)} {a.detail && <span className="text-sam-slate">({a.detail})</span>}</span>
                    <span className="text-sam-slate shrink-0">{new Date(a.at).toLocaleString()}</span>
                  </div>
                ))}
                {acts.has_more && <Button size="sm" variant="outline" className="mt-2" onClick={() => loadActs(actSkip + 20)} data-testid="owner-activity-more">Load more</Button>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SamLeads({ business }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && !data) {
      setLoading(true);
      try { const r = await api.get(`/my/businesses/${business.id}/sam-leads`); setData(r.data); }
      catch { setData({ leads: [], qr_scans: 0 }); } finally { setLoading(false); }
    }
  };
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`myleads-${business.slug}`}>
      <button type="button" onClick={toggle} className="inline-flex items-center gap-1.5 text-sm font-semibold text-sam-navy hover:text-sam-crimson" data-testid={`myleads-toggle-${business.slug}`}>
        <Inbox className="h-4 w-4" />My Leads{typeof business.qr_scans === "number" && business.qr_scans > 0 ? ` · ${business.qr_scans} QR scans` : ""}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin text-sam-crimson" /> : (
            <>
              <div className="text-xs text-sam-slate mb-2">QR scans: <span className="font-semibold text-sam-navy">{data?.qr_scans ?? 0}</span>. Full follow-up tools (calls, texts, AI) live in your marketing dashboard — check your email invite to log in.</div>
              {data?.leads?.length ? (
                <ul className="space-y-1.5">
                  {data.leads.slice(0, 15).map((l) => (
                    <li key={l.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`myleads-row-${l.id}`}>
                      <span className="truncate"><span className="font-semibold text-sam-navy">{l.name || l.email || l.phone || "Lead"}</span> <span className="text-sam-slate text-xs">· {l.source}</span></span>
                      <span className="text-xs text-sam-slate whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-sam-slate">No leads yet. Leads from your listing and postcard QR code show up here.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
